import { clearAllBodyScrollLocks, disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'
import { AnimatePresence, motion } from 'framer-motion'
import dynamic from 'next/dynamic'
import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { CircularCrossIcon } from 'icons/CircularCross'
import { media } from 'utils/media'
import { useEscapeKey } from 'utils/useEscapeKey'
import { Text } from 'lib/Text'
import { CarouselItem } from 'modules/brands/show/components/Carousel'

const ImageCarousel = dynamic(() => import('modules/brands/show/components/Carousel').then((res) => res.BrandImageCarousel))
const Portal = dynamic(() => import('lib/Portal').then((res) => res.Portal))
const FocusLock = dynamic(() => import('react-focus-lock'))

const Container = styled(motion.div)`
  background: rgba(0, 0, 0, 1);
  bottom: 0;
  display: block;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 1000;
`

const CloseButton = styled.button<{ withTitle: boolean }>`
  align-items: center;
  appearance: none;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  gap: 8px;
  outline: none;
  padding: 0;
  position: absolute;
  right: 40px;
  top: 40px;
  z-index: 100;
  ${media.phoneDown} {
    right: 16px;
    top: 20px;
  }
`

const CrossIcon = styled(CircularCrossIcon)`
  width: 20px;

  path {
    fill: var(--neutral-40);
    transition: fill 0.2s ease-in-out;
  }

  &:hover path {
    fill: black;
  }
`

interface Props {
  open: boolean
  onRequestClose?: () => void
  title?: React.ReactNode
  showBorder?: boolean
  mobileFullscreen?: boolean
  showClose?: boolean
  className?: string
  images: CarouselItem[]
  index: number | null
}

export const GalleryPopup = ({ open, images, onRequestClose = () => {}, title, showClose = true, index = 0 }: Props) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [contentRefVisible, setContentRefVisible] = useState<boolean>(false)

  useEscapeKey(onRequestClose, open)

  useEffect(() => {
    if (contentRefVisible && containerRef.current) {
      if (open) {
        disableBodyScroll(containerRef.current)
      } else {
        enableBodyScroll(containerRef.current)
      }
    }

    return () => {
      clearAllBodyScrollLocks()
    }
  }, [open, contentRefVisible])

  const handleContainerClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === containerRef.current) {
      e.preventDefault()
      onRequestClose()
    }
  }

  const handleCloseClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    onRequestClose()
  }

  return (
    <AnimatePresence>
      {open && (
        <Portal>
          <FocusLock disabled={false}>
            <Container
              transition={{ scale: { ease: 'easeOut', duration: 0.2 } }}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={handleContainerClick}
              ref={(element) => {
                containerRef.current = element
                setContentRefVisible(!!element)
              }}>
              <ImageCarousel initialSlide={index} title="Nada" id={`${title}-carousel`} items={images} />

              {showClose && (
                <CloseButton aria-label="Close" onClick={handleCloseClick} withTitle={!!title}>
                  <Text color="white" weight="semi" size="pxs">
                    Close
                  </Text>
                  <CrossIcon />
                </CloseButton>
              )}
            </Container>
          </FocusLock>
        </Portal>
      )}
    </AnimatePresence>
  )
}
